import { useQueryClient } from '@tanstack/react-query'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { getWaterQualityKey } from 'common/constants/queryKeys'
import useMedia from 'common/hooks/useMedia'
import { useGetNotificationsSettingsByModule } from 'modules/notification/api/queries'
import { useUserInfo } from 'modules/user/api/queries'
import { usePermitByFeature, usePermitToActionFeature } from 'modules/user/api/selector'
import { useGetOldestIotDeviceByPond, useGetWaterQualityToday } from 'modules/water/api/queries'
import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, ClickAwayListener, Grid, Typography, alpha, useTheme } from '@mui/material'

import { RecordFeatureButton } from 'components/RecordFeatureButton'

import BacteriaChart from '../BacteriaChart'
import MineralChart from '../MineralChart'
import QualityChart from '../QualityChart'
import QualityTodayTable from '../QualityTodayTable'
import RecordQualityOption from '../RecordQualityOption'
import WaterIotTab from '../WaterIotTab'
import WaterTable from '../WaterTable'

interface Option {
  id: number
  label: string
}

interface WaterQualityValue {
  value: number | null
  isOutOfRange: boolean
}

interface ProcessedTodayData {
  morning: Record<string, WaterQualityValue>
  evening: Record<string, WaterQualityValue>
}

const Water = () => {
  const isMobile = useMedia('sm')
  const { t } = useTranslation(['water'])
  const qc = useQueryClient()

  const { data: todayData } = useGetWaterQualityToday()
  const {
    data: iotDevice,
    isError: iotError,
    isLoading: iotLoading,
  } = useGetOldestIotDeviceByPond()

  const hasIotDevice = !iotError && !iotLoading && !!iotDevice
  const { isLoading: isUserLoading } = useUserInfo()
  const canAccessIot = usePermitByFeature(FeatureKey.Iot)

  const [tab, setTab] = useState<0 | 1>(0)

  useEffect(() => {
    // usePermitByFeature reads from useUserInfo() and defaults to `false`
    // while that's still loading — wait for it, otherwise every load
    // momentarily looks permission-less and force-switches to manual,
    // with nothing to ever switch it back once the real permission (true)
    // resolves.
    if (isUserLoading) return
    // No IoT permission at all — skip the device/manual choice entirely
    // and go straight to the manual-entry view, same as the existing
    // no-device fallback below.
    if (!canAccessIot || (!iotLoading && !hasIotDevice)) {
      setTab(1)
    }
  }, [canAccessIot, iotLoading, hasIotDevice, isUserLoading])

  const { data: notiSettingsData } = useGetNotificationsSettingsByModule('water_quality')

  const processedTodayData = useMemo(() => {
    if (!todayData || !notiSettingsData?.data?.water_quality) return null

    const waterQualitySettings = notiSettingsData.data.water_quality

    const checkThreshold = (value: number | null, subModuleCode: string): boolean => {
      if (value === null) return false

      const settings = waterQualitySettings[subModuleCode]
      if (!settings || !settings.is_enabled || !settings.thresholds) return false

      const { min, max } = settings.thresholds
      const minValue = typeof min === 'number' ? min : parseFloat(min)
      const maxValue = typeof max === 'number' ? max : parseFloat(max)

      return value < minValue || value > maxValue
    }

    const processTimeData = (timeData: object) => {
      const processed: Record<string, WaterQualityValue> = {}

      Object.entries(timeData).forEach(([key, val]) => {
        const subModuleCode =
          key.toLowerCase() === 'do'
            ? 'do'
            : key.toLowerCase() === 'terbidity'
            ? 'turbidity'
            : key.toLowerCase()

        const value = val as number | null

        processed[key] = {
          value,
          isOutOfRange: checkThreshold(value, subModuleCode),
        }
      })

      return processed
    }

    return {
      morning: processTimeData(todayData.morning),
      evening: processTimeData(todayData.evening),
    } as ProcessedTodayData
  }, [todayData, notiSettingsData])

  const showQuality = usePermitByFeature(FeatureKey.WaterQuality)
  const createQuality = usePermitToActionFeature([CRUDAction.Create], FeatureKey.WaterQuality)

  const showMineral = usePermitByFeature(FeatureKey.WaterMineral)
  const createMineral = usePermitToActionFeature([CRUDAction.Create], FeatureKey.WaterMineral)

  const showVibrio = usePermitByFeature(FeatureKey.WaterVibrio)
  const createVibrio = usePermitToActionFeature([CRUDAction.Create], FeatureKey.WaterVibrio)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Option | null>(null)

  const menuOptions: Option[] = [
    createQuality && { id: 1, label: t('water:water.quality.report') },
    createMineral && { id: 2, label: t('water:mineral') },
    createVibrio && { id: 3, label: t('water:bacteria.in.water') },
  ].filter(Boolean) as Option[]

  const handleOptionClick = (option: Option) => {
    setSelectedOption(option)
    setIsDialogOpen(true)
    setIsOptionsOpen(false)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setSelectedOption(null)
  }

  const handleInvalidateQueries = async () => {
    await qc.invalidateQueries({ queryKey: getWaterQualityKey.all() })
  }

  const theme = useTheme()

  const pillTab = (label: string, value: 0 | 1, disabled = false) => {
    const active = tab === value
    return (
      <Box
        component="button"
        onClick={() => !disabled && setTab(value)}
        sx={{
          px: 2.5,
          py: 0.75,
          borderRadius: 1,
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: active ? 'primary.main' : 'transparent',
          opacity: disabled ? 0.4 : 1,
          transition: 'background-color 0.15s',
          '&:hover':
            !disabled && !active ? { bgcolor: alpha(theme.palette.primary.main, 0.08) } : {},
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: active ? 'common.white' : 'text.primary', lineHeight: 1.5 }}
        >
          {label}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ py: isMobile ? 0 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canAccessIot && pillTab(t('water:device'), 0, !hasIotDevice)}
          {canAccessIot && pillTab(t('water:manual_record'), 1)}
        </Box>

        {tab === 1 && (
          <RecordFeatureButton
            buttonText={t('water:record.quality')}
            onAfterOpen={() => setIsOptionsOpen(!isOptionsOpen)}
            customComponent={
              isOptionsOpen && (
                <ClickAwayListener onClickAway={() => setIsOptionsOpen(false)}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      width: 165,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      zIndex: 10,
                      borderRadius: 2,
                      overflow: 'hidden',
                      mt: 1,
                    }}
                  >
                    {menuOptions.map((option) => (
                      <Box
                        key={option.id}
                        onClick={() => handleOptionClick(option)}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        {option.label}
                      </Box>
                    ))}
                  </Box>
                </ClickAwayListener>
              )
            }
            dialog={() => (
              <RecordQualityOption
                open={isDialogOpen}
                onClose={handleCloseDialog}
                selectedOption={selectedOption}
              />
            )}
            showButton={createQuality || createMineral || createVibrio}
          />
        )}
      </Box>

      {canAccessIot && tab === 0 && iotDevice && <WaterIotTab device={iotDevice} />}

      {tab === 1 && (
        <Grid container spacing={2} sx={{ height: 'max-content' }}>
          {processedTodayData && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: '20px',
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 400 }}>
                  <QualityTodayTable data={processedTodayData} tableNumber={1} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 400 }}>
                  <QualityTodayTable data={processedTodayData} tableNumber={2} />
                </Box>
              </Box>
            </Grid>
          )}

          {showQuality && (
            <Grid item xs={12}>
              <QualityChart />
            </Grid>
          )}
          {showMineral && (
            <Grid item xs={12}>
              <MineralChart />
            </Grid>
          )}
          {showVibrio && (
            <Grid item xs={12}>
              <BacteriaChart />
            </Grid>
          )}
          <Grid item xs={12}>
            <WaterTable refetchToday={handleInvalidateQueries} />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default Water
